import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../../components/Layout';
import BopBackLink from '../../../components/BopBackLink';
import Modal from '../../../components/Modal';
import ApiInspector from '../../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import { apiFetch } from '../../../lib/api';
import { usePermesso } from '../../../context/PermessiContext';
import { useBloccoRecordContesto } from '../../../hooks/useBloccoRecordContesto';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import { TIPO_CAUSALE_LABEL } from './CausaliContabiliPage';
import { API_DOCS } from './CausaleContabileDettaglioPage.api';
import './CausaliContabili.css';

const MODULO = 'amministrazione.causali_contabili';

type Segno = 'DARE' | 'AVERE';
type Origine = 'FISSO' | 'DA_ANAGRAFICA' | 'DA_CODICE_IVA' | 'SCELTA_UTENTE';
type Formula = 'IMPONIBILE' | 'IVA' | 'TOTALE' | 'MANUALE' | 'PERCENTUALE';

const SEGNO_LABEL: Record<Segno, string> = { DARE: 'Dare', AVERE: 'Avere' };
const ORIGINE_LABEL: Record<Origine, string> = {
  FISSO: 'Conto fisso',
  DA_ANAGRAFICA: 'Da anagrafica (cliente/fornitore)',
  DA_CODICE_IVA: 'Da codice IVA',
  SCELTA_UTENTE: 'Scelta utente al momento',
};
const FORMULA_LABEL: Record<Formula, string> = {
  IMPONIBILE: 'Imponibile',
  IVA: 'IVA',
  TOTALE: 'Totale',
  MANUALE: 'Manuale (inserito dall\'utente)',
  PERCENTUALE: '% su imponibile',
};

const ArrowUp = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="18 15 12 9 6 15" />
  </svg>
);
const ArrowDown = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

interface ContoNode {
  id: number;
  codice: string;
  descrizione: string;
  movimentabile: boolean;
  attivo: boolean;
  figli: ContoNode[];
}

interface ContoFlat { id: number; codice: string; descrizione: string }

interface SezionaleIvaCombo {
  id: number; codice: string; descrizione: string; attivo: boolean;
}
interface RegistroCombo {
  id: number; codice: string; descrizione: string; attivo: boolean;
}

interface RigaCausale {
  id: number;
  causaleId: number;
  ordine: number;
  descrizione: string;
  segno: Segno;
  origineConto: Origine;
  contoCogeId: number | null;
  conto: { id: number; codice: string; descrizione: string; movimentabile: boolean } | null;
  formula: Formula;
  percentuale: number | null;
  obbligatoria: boolean;
}

interface CausaleDettaglio {
  id: number;
  societaId: number;
  codice: string;
  descrizione: string;
  tipo: keyof typeof TIPO_CAUSALE_LABEL;
  gestisceIva: boolean;
  sezionaleIvaDefaultId: number | null;
  registroContabileDefaultId: number | null;
  isDefault: boolean;
  attivo: boolean;
  note: string | null;
  righe: RigaCausale[];
  daCompletare: boolean;
  dettagliDaCompletare: { righeFissoSenzaConto: number; mancaSezionale: boolean; mancaRegistro: boolean };
}

interface FormIntestazione {
  codice: string;
  descrizione: string;
  gestisceIva: boolean;
  sezionaleIvaDefaultId: string;
  registroContabileDefaultId: string;
  isDefault: boolean;
  note: string;
}

interface ModalRigaState {
  mode: 'create' | 'edit';
  riga: RigaCausale | null;
}

interface FormRiga {
  descrizione: string;
  segno: Segno;
  origineConto: Origine;
  contoCogeId: string;
  formula: Formula;
  percentuale: string;
  obbligatoria: boolean;
}

// Appiattisce l'albero conti CO.GE in lista di sole foglie movimentabili.
function flattenLeaves(tree: ContoNode[]): ContoFlat[] {
  const out: ContoFlat[] = [];
  const rec = (nodes: ContoNode[]) => {
    for (const n of nodes) {
      if (n.movimentabile && n.attivo) {
        out.push({ id: n.id, codice: n.codice, descrizione: n.descrizione });
      }
      if (n.figli?.length) rec(n.figli);
    }
  };
  rec(tree);
  out.sort((a, b) => a.codice.localeCompare(b.codice, undefined, { numeric: true }));
  return out;
}

export default function CausaleContabileDettaglioPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = idParam ? parseInt(idParam, 10) : NaN;
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');
  const canDelete = can(MODULO, 'delete');

  const [causale, setCausale] = useState<CausaleDettaglio | null>(null);
  const [conti, setConti] = useState<ContoFlat[]>([]);
  const [sezionali, setSezionali] = useState<SezionaleIvaCombo[]>([]);
  const [registri, setRegistri] = useState<RegistroCombo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form intestazione
  const [form, setForm] = useState<FormIntestazione>({
    codice: '', descrizione: '', gestisceIva: false,
    sezionaleIvaDefaultId: '', registroContabileDefaultId: '',
    isDefault: false, note: '',
  });
  const [initialSnapshot, setInitialSnapshot] = useState<string>('');
  const isDirty = initialSnapshot !== '' && JSON.stringify(form) !== initialSnapshot;
  const [savingHeader, setSavingHeader] = useState(false);
  const [headerError, setHeaderError] = useState('');

  // Modale riga
  const [modalRiga, setModalRiga] = useState<ModalRigaState | null>(null);
  const [formRiga, setFormRiga] = useState<FormRiga>({
    descrizione: '', segno: 'DARE', origineConto: 'FISSO', contoCogeId: '',
    formula: 'TOTALE', percentuale: '', obbligatoria: true,
  });
  const [rigaError, setRigaError] = useState('');
  const [savingRiga, setSavingRiga] = useState(false);

  // Hook contesto e dirty
  useBloccoRecordContesto({
    recordSocietaId: causale?.societaId,
    label: causale ? `Causale ${causale.codice}` : 'Causale contabile',
  });
  useDirtyForm(isDirty, `Causale ${causale?.codice ?? ''}`.trim() || 'Causale contabile');

  // ── Caricamento ────────────────────────────────────────────────────────────
  // silent=true: ricarica dopo azione (no full-page spinner, evita scroll-to-top)
  const load = async (silent = false) => {
    if (!Number.isFinite(id)) { setError('ID causale non valido'); setLoading(false); return; }
    if (!silent) setLoading(true);
    setError('');
    try {
      // @api GET /api/causali-contabili/:id -> { success: true, data: { ..., righe: [...] }, logs: [...] }
      const res = await apiFetch<{ success: boolean; data: CausaleDettaglio }>(`/api/causali-contabili/${id}`);
      const c = res.data;
      setCausale(c);
      const initialForm: FormIntestazione = {
        codice: c.codice,
        descrizione: c.descrizione,
        gestisceIva: c.gestisceIva,
        sezionaleIvaDefaultId: c.sezionaleIvaDefaultId ? String(c.sezionaleIvaDefaultId) : '',
        registroContabileDefaultId: c.registroContabileDefaultId ? String(c.registroContabileDefaultId) : '',
        isDefault: c.isDefault,
        note: c.note || '',
      };
      setForm(initialForm);
      setInitialSnapshot(JSON.stringify(initialForm));

      // Carica combo dipendenti dalla società
      const sid = c.societaId;
      // @api GET /api/coge/piano-conti?societaId= -> { success: true, data: [tree] }
      const tree = await apiFetch<{ success: boolean; data: ContoNode[] }>(`/api/coge/piano-conti?societaId=${sid}`);
      // @api GET /api/sezionali-iva?societaId= -> { success: true, data: [{ id, codice, descrizione, tipo, attivo }] }
      const sez = await apiFetch<{ success: boolean; data: SezionaleIvaCombo[] }>(`/api/sezionali-iva?societaId=${sid}`);
      // @api GET /api/registri-contabili?societaId= -> { success: true, data: [{ id, codice, descrizione, tipo, attivo }] }
      const reg = await apiFetch<{ success: boolean; data: RegistroCombo[] }>(`/api/registri-contabili?societaId=${sid}`);
      setConti(flattenLeaves(tree.data ?? []));
      setSezionali(sez.data ?? []);
      setRegistri(reg.data ?? []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(false); }, [id]);

  // ── Combo sezionali IVA (tutti gli attivi: il sezionale non ha tipologia) ──
  const sezionaliAttivi = useMemo(() => sezionali.filter(s => s.attivo), [sezionali]);

  const registriAttivi = useMemo(() => registri.filter(r => r.attivo), [registri]);

  // ── Salva intestazione ─────────────────────────────────────────────────────
  const salvaIntestazione = async () => {
    if (!causale) return;
    const codice = form.codice.trim();
    const descrizione = form.descrizione.trim();
    if (!codice) { setHeaderError('Codice obbligatorio'); return; }
    if (!descrizione) { setHeaderError('Descrizione obbligatoria'); return; }

    setSavingHeader(true);
    setHeaderError('');
    try {
      const payload: Record<string, unknown> = {
        codice, descrizione,
        gestisceIva: form.gestisceIva,
        sezionaleIvaDefaultId: form.sezionaleIvaDefaultId ? Number(form.sezionaleIvaDefaultId) : null,
        registroContabileDefaultId: form.registroContabileDefaultId ? Number(form.registroContabileDefaultId) : null,
        note: form.note.trim() || null,
      };
      // @api PUT /api/causali-contabili/:id { codice, descrizione, gestisceIva, sezionaleIvaDefaultId, registroContabileDefaultId, note } -> { success: true }
      await apiFetch(`/api/causali-contabili/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      // isDefault separato
      if (form.isDefault !== causale.isDefault) {
        // @api PATCH /api/causali-contabili/:id/default { isDefault } -> { success: true }
        await apiFetch(`/api/causali-contabili/${id}/default`, {
          method: 'PATCH',
          body: JSON.stringify({ isDefault: form.isDefault }),
        });
      }

      setInitialSnapshot(JSON.stringify(form));
      await load(true);
    } catch (e: any) {
      setHeaderError(e.message || 'Errore nel salvataggio');
    } finally {
      setSavingHeader(false);
    }
  };

  // ── Modale riga ────────────────────────────────────────────────────────────
  const openCreaRiga = () => {
    setModalRiga({ mode: 'create', riga: null });
    setFormRiga({ descrizione: '', segno: 'DARE', origineConto: 'FISSO', contoCogeId: '', formula: 'TOTALE', percentuale: '', obbligatoria: true });
    setRigaError('');
  };

  const openEditRiga = (r: RigaCausale) => {
    setModalRiga({ mode: 'edit', riga: r });
    setFormRiga({
      descrizione: r.descrizione,
      segno: r.segno,
      origineConto: r.origineConto,
      contoCogeId: r.contoCogeId ? String(r.contoCogeId) : '',
      formula: r.formula,
      percentuale: r.percentuale != null ? String(r.percentuale) : '',
      obbligatoria: r.obbligatoria,
    });
    setRigaError('');
  };

  const closeModalRiga = () => { setModalRiga(null); setRigaError(''); };

  const salvaRiga = async () => {
    if (!modalRiga || !causale) return;
    const descrizione = formRiga.descrizione.trim();
    if (!descrizione) { setRigaError('Descrizione obbligatoria'); return; }
    if (formRiga.origineConto === 'FISSO' && !formRiga.contoCogeId) {
      setRigaError('Per origine FISSO seleziona un conto del piano CO.GE (o lascia vuoto per "da completare")');
      // permettiamo null
    }
    if (formRiga.formula === 'PERCENTUALE') {
      const p = Number(formRiga.percentuale);
      if (!Number.isFinite(p) || p < 0 || p > 100) {
        setRigaError('Per formula PERCENTUALE inserisci una percentuale tra 0 e 100');
        return;
      }
    }

    setSavingRiga(true);
    setRigaError('');
    try {
      const payload: Record<string, unknown> = {
        descrizione,
        segno: formRiga.segno,
        origineConto: formRiga.origineConto,
        contoCogeId: formRiga.origineConto === 'FISSO' && formRiga.contoCogeId ? Number(formRiga.contoCogeId) : null,
        formula: formRiga.formula,
        percentuale: formRiga.formula === 'PERCENTUALE' ? Number(formRiga.percentuale) : null,
        obbligatoria: formRiga.obbligatoria,
      };
      if (modalRiga.mode === 'create') {
        // @api POST /api/causali-contabili/:id/righe { descrizione, segno, origineConto, contoCogeId, formula, percentuale, obbligatoria } -> { success: true, data: { id } }
        await apiFetch(`/api/causali-contabili/${id}/righe`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      } else if (modalRiga.riga) {
        // @api PUT /api/causali-contabili/:id/righe/:rigaId { descrizione, segno, origineConto, contoCogeId, formula, percentuale, obbligatoria } -> { success: true }
        await apiFetch(`/api/causali-contabili/${id}/righe/${modalRiga.riga.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      }
      closeModalRiga();
      load(true);
    } catch (e: any) {
      setRigaError(e.message || 'Errore nel salvataggio');
    } finally {
      setSavingRiga(false);
    }
  };

  const eliminaRiga = async (r: RigaCausale) => {
    if (!window.confirm(`Eliminare la riga "${r.descrizione}"?`)) return;
    try {
      // @api DELETE /api/causali-contabili/:id/righe/:rigaId -> { success: true }
      await apiFetch(`/api/causali-contabili/${id}/righe/${r.id}`, { method: 'DELETE' });
      load(true);
    } catch (e: any) {
      window.alert(e.message || 'Errore');
    }
  };

  const spostaRiga = async (r: RigaCausale, delta: -1 | 1) => {
    if (!causale) return;
    const righe = causale.righe.slice().sort((a, b) => a.ordine - b.ordine);
    const idx = righe.findIndex(x => x.id === r.id);
    if (idx < 0) return;
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= righe.length) return;
    const reordered = righe.slice();
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);
    try {
      // @api PATCH /api/causali-contabili/:id/righe/riordina { ordineIds } -> { success: true }
      await apiFetch(`/api/causali-contabili/${id}/righe/riordina`, {
        method: 'PATCH',
        body: JSON.stringify({ ordineIds: reordered.map(x => x.id) }),
      });
      load(true);
    } catch (e: any) {
      window.alert(e.message || 'Errore nel riordino');
    }
  };

  if (loading) return <Layout><div className="bop-empty-state">Caricamento…</div></Layout>;
  if (error) return <Layout><div className="bop-inline-error">{error}</div></Layout>;
  if (!causale) return <Layout><div className="bop-empty-state">Causale non trovata.</div></Layout>;

  const righeOrd = causale.righe.slice().sort((a, b) => a.ordine - b.ordine);

  return (
    <Layout>
      <div className="causali-contabili">
        <BopBackLink to="/amministrazione/causali-contabili" label="Torna alle causali" />
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2>
              Causale {causale.codice}
              {causale.isDefault && <span className="cc-badge-default" title="Default per tipo">★</span>}
            </h2>
            <p>
              {TIPO_CAUSALE_LABEL[causale.tipo]}
              {causale.gestisceIva && <> · <strong>gestione IVA</strong></>}
              {causale.daCompletare && <span className="cc-badge-todo cc-badge-todo--inline">Da completare</span>}
            </p>
          </div>
        </div>

        {/* INTESTAZIONE */}
        <div className="bop-card cc-intestazione">
          <div className="cc-card-header">
            <h3>Intestazione</h3>
            {canEdit && (
              <button className="bop-btn bop-btn-primary" onClick={salvaIntestazione} disabled={!isDirty || savingHeader}>
                {savingHeader ? 'Salvo…' : 'Salva intestazione'}
              </button>
            )}
          </div>
          {headerError && <div className="bop-inline-error">{headerError}</div>}

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Codice *</label>
              <input
                className="bop-input"
                value={form.codice}
                disabled={!canEdit}
                onChange={e => setForm(f => ({ ...f, codice: e.target.value }))}
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Tipo</label>
              <input className="bop-input" value={TIPO_CAUSALE_LABEL[causale.tipo]} readOnly />
            </div>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Descrizione *</label>
            <input
              className="bop-input"
              value={form.descrizione}
              disabled={!canEdit}
              onChange={e => setForm(f => ({ ...f, descrizione: e.target.value }))}
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-flex cc-checkbox-label">
              <input
                type="checkbox"
                checked={form.gestisceIva}
                disabled={!canEdit}
                onChange={e => setForm(f => ({ ...f, gestisceIva: e.target.checked }))}
              />
              <span>La causale gestisce IVA</span>
            </label>
          </div>

          <div className="bop-form-row">
            {form.gestisceIva && (
              <div className="bop-form-group">
                <label className="bop-label">Sezionale IVA default</label>
                <select
                  className="bop-select"
                  value={form.sezionaleIvaDefaultId}
                  disabled={!canEdit}
                  onChange={e => setForm(f => ({ ...f, sezionaleIvaDefaultId: e.target.value }))}
                >
                  <option value="">— non impostato —</option>
                  {sezionaliAttivi.map(s => (
                    <option key={s.id} value={s.id}>{s.codice} — {s.descrizione}</option>
                  ))}
                </select>
                {sezionaliAttivi.length === 0 && (
                  <p className="cc-hint">
                    Nessun sezionale IVA attivo per questa società. Creane uno in Sezionali IVA.
                  </p>
                )}
              </div>
            )}
            <div className="bop-form-group">
              <label className="bop-label">Registro contabile default</label>
              <select
                className="bop-select"
                value={form.registroContabileDefaultId}
                disabled={!canEdit}
                onChange={e => setForm(f => ({ ...f, registroContabileDefaultId: e.target.value }))}
              >
                <option value="">— non impostato —</option>
                {registriAttivi.map(r => (
                  <option key={r.id} value={r.id}>{r.codice} — {r.descrizione}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="bop-form-group">
            <label className="bop-flex cc-checkbox-label">
              <input
                type="checkbox"
                checked={form.isDefault}
                disabled={!canEdit || !causale.attivo}
                onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))}
              />
              <span>Imposta come causale di default per il tipo <strong>{TIPO_CAUSALE_LABEL[causale.tipo]}</strong></span>
            </label>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Note</label>
            <textarea
              className="bop-input"
              rows={2}
              value={form.note}
              disabled={!canEdit}
              onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
            />
          </div>
        </div>

        {/* RIGHE */}
        <div className="bop-card cc-righe">
          <div className="cc-card-header">
            <h3>Righe della scrittura ({righeOrd.length})</h3>
            {canEdit && (
              <button className="bop-btn bop-btn-primary" onClick={openCreaRiga}>+ Aggiungi riga</button>
            )}
          </div>

          {righeOrd.length === 0 ? (
            <div className="bop-empty-state">Nessuna riga definita. Aggiungi almeno una riga DARE e una AVERE.</div>
          ) : (
            <div className="bop-table-wrapper">
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Descrizione</th>
                    <th className="bop-text-center">Segno</th>
                    <th>Origine conto</th>
                    <th>Conto / Riferimento</th>
                    <th>Importo</th>
                    <th className="bop-text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {righeOrd.map((r, i) => (
                    <tr key={r.id} className={r.origineConto === 'FISSO' && !r.contoCogeId ? 'cc-row--incomplete' : ''}>
                      <td className="cc-ordine">{i + 1}</td>
                      <td>{r.descrizione}</td>
                      <td className="bop-text-center">
                        <span className={`bop-badge ${r.segno === 'DARE' ? 'bop-badge--info' : 'bop-badge--warning'}`}>
                          {SEGNO_LABEL[r.segno]}
                        </span>
                      </td>
                      <td>{ORIGINE_LABEL[r.origineConto]}</td>
                      <td>
                        {r.origineConto === 'FISSO' ? (
                          r.conto ? (
                            <span><strong>{r.conto.codice}</strong> {r.conto.descrizione}</span>
                          ) : (
                            <span className="cc-badge-todo">conto da impostare</span>
                          )
                        ) : (
                          <span className="bop-text-muted">{ORIGINE_LABEL[r.origineConto]}</span>
                        )}
                      </td>
                      <td>
                        {r.formula === 'PERCENTUALE' && r.percentuale != null
                          ? `${FORMULA_LABEL.PERCENTUALE} (${r.percentuale}%)`
                          : FORMULA_LABEL[r.formula]}
                      </td>
                      <td className="bop-text-right">
                        <BopRowActions actions={[
                          { icon: <ArrowUp />, label: 'Sposta su', onClick: () => spostaRiga(r, -1), hidden: !canEdit || i === 0 },
                          { icon: <ArrowDown />, label: 'Sposta giù', onClick: () => spostaRiga(r, 1), hidden: !canEdit || i === righeOrd.length - 1 },
                          { icon: <RowIcon.Edit />, label: 'Modifica', onClick: () => openEditRiga(r), hidden: !canEdit },
                          { icon: <RowIcon.Trash />, label: 'Elimina', onClick: () => eliminaRiga(r), variant: 'danger', hidden: !canDelete },
                        ]} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* MODALE RIGA */}
      {modalRiga && (
        <Modal
          title={modalRiga.mode === 'edit' ? 'Modifica riga' : 'Nuova riga'}
          onClose={closeModalRiga}
          size="lg"
          footer={
            <>
              <button className="bop-btn bop-btn-secondary" onClick={closeModalRiga}>Annulla</button>
              <button className="bop-btn bop-btn-primary" onClick={salvaRiga} disabled={savingRiga}>
                {savingRiga ? 'Salvo…' : 'Salva riga'}
              </button>
            </>
          }
        >
          {rigaError && <div className="bop-inline-error">{rigaError}</div>}

          <div className="bop-form-group">
            <label className="bop-label">Descrizione riga *</label>
            <input
              className="bop-input"
              value={formRiga.descrizione}
              onChange={e => setFormRiga(f => ({ ...f, descrizione: e.target.value }))}
              placeholder="es. Imponibile, IVA, Fornitore, Banca…"
              autoFocus
            />
          </div>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Segno *</label>
              <select
                className="bop-select"
                value={formRiga.segno}
                onChange={e => setFormRiga(f => ({ ...f, segno: e.target.value as Segno }))}
              >
                <option value="DARE">Dare</option>
                <option value="AVERE">Avere</option>
              </select>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Origine del conto *</label>
              <select
                className="bop-select"
                value={formRiga.origineConto}
                onChange={e => setFormRiga(f => ({ ...f, origineConto: e.target.value as Origine, contoCogeId: e.target.value === 'FISSO' ? f.contoCogeId : '' }))}
              >
                {(Object.keys(ORIGINE_LABEL) as Origine[]).map(o => (
                  <option key={o} value={o}>{ORIGINE_LABEL[o]}</option>
                ))}
              </select>
            </div>
          </div>

          {formRiga.origineConto === 'FISSO' && (
            <div className="bop-form-group">
              <label className="bop-label">Conto del piano CO.GE</label>
              <select
                className="bop-select"
                value={formRiga.contoCogeId}
                onChange={e => setFormRiga(f => ({ ...f, contoCogeId: e.target.value }))}
              >
                <option value="">— da completare in seguito —</option>
                {conti.map(c => (
                  <option key={c.id} value={c.id}>{c.codice} — {c.descrizione}</option>
                ))}
              </select>
              <p className="cc-hint">
                Solo sottoconti movimentabili attivi del piano CO.GE.
                Se non lo imposti ora, la causale rimarrà "Da completare" e non potrà essere usata per generare scritture reali.
              </p>
            </div>
          )}

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Formula importo *</label>
              <select
                className="bop-select"
                value={formRiga.formula}
                onChange={e => setFormRiga(f => ({ ...f, formula: e.target.value as Formula }))}
              >
                {(Object.keys(FORMULA_LABEL) as Formula[]).map(fm => (
                  <option key={fm} value={fm}>{FORMULA_LABEL[fm]}</option>
                ))}
              </select>
            </div>
            {formRiga.formula === 'PERCENTUALE' && (
              <div className="bop-form-group">
                <label className="bop-label">Percentuale (0-100)</label>
                <input
                  className="bop-input"
                  type="number"
                  step="0.0001"
                  min="0"
                  max="100"
                  value={formRiga.percentuale}
                  onChange={e => setFormRiga(f => ({ ...f, percentuale: e.target.value }))}
                  placeholder="es. 20 per ritenuta 20%"
                />
              </div>
            )}
          </div>

          <div className="bop-form-group">
            <label className="bop-flex cc-checkbox-label">
              <input
                type="checkbox"
                checked={formRiga.obbligatoria}
                onChange={e => setFormRiga(f => ({ ...f, obbligatoria: e.target.checked }))}
              />
              <span>Riga obbligatoria nella scrittura</span>
            </label>
            <p className="cc-hint">
              Se non obbligatoria, la riga sarà opzionale al momento della registrazione (es. ritenute non sempre presenti).
            </p>
          </div>
        </Modal>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
